import { useCallback } from 'react'
import { parsedSdkErrorBody, sdkErrorMessage, useAuthServiceClient, type SdkThrownError } from './sdk-client'

export interface BindIdentifierResult {
  bound: boolean
}

// `@ideon-api/sdk-internal` generates per-status error classes
// (AuthErrorResponse, AuthIdentifierConflictResponse, ...) and documents them
// in its `.d.ts`, but does not include them in the package's runtime
// `export {}` list -- `import { AuthIdentifierConflictResponse } from
// '@ideon-api/sdk-internal'` resolves to `undefined` (verified against the
// installed 0.27.0 bundle), so `instanceof` against them throws at runtime
// instead of narrowing. Branch on the thrown object's `metadata.status` and
// read fields structurally instead -- both are real properties on whatever
// the SDK actually throws, unlike the unexported class references.
//
// The dialog's only job is to render `.message` -- every error shape (typed
// 409/403, or a raw network/abort failure) is translated here into a single
// rejected Error with an already-friendly message.
export function useBindIdentifier(): {
  bindIdentifier: (slug: string, threescaleAppId: string) => Promise<BindIdentifierResult>
} {
  const getClient = useAuthServiceClient()

  const bindIdentifier = useCallback(
    async (slug: string, threescaleAppId: string): Promise<BindIdentifierResult> => {
      try {
        const client = await getClient()
        const response = await client.auth.bindAccountIdentifier(slug, {
          identifierType: 'threescale_app_id',
          value: threescaleAppId,
          environment: 'production',
        })
        return { bound: response.data?.bound ?? false }
      } catch (error) {
        const status =
          error && typeof error === 'object' ? (error as SdkThrownError).metadata?.status : undefined

        if (status === 409) {
          // The server's verbatim (snake_case) body -- the SDK's own
          // camelCase fields never populate, since the typed error classes
          // that would set them never run against this API's responses
          // (see parsedSdkErrorBody's comment in sdk-client.ts).
          const ownerSlug = parsedSdkErrorBody(error).body.owner_slug
          throw new Error(
            typeof ownerSlug === 'string' && ownerSlug
              ? `Already bound to account "${ownerSlug}".`
              : 'Already bound to a different account.'
          )
        }
        if (status === 403) {
          throw new Error("You don't have permission to bind identifiers.")
        }
        // A client-side abort/timeout can't roll back a bind that already
        // committed server-side -- same reasoning as ConfirmMergeDialog's
        // handleMerge -- so treat it as "unknown", not a plain failure.
        // `AbortSignal.timeout()` (used once `timeoutMs` is set on the SDK
        // client) raises a `TimeoutError`, not `AbortError` -- match both.
        if (error instanceof DOMException && (error.name === 'TimeoutError' || error.name === 'AbortError')) {
          throw new Error('This may have completed -- refresh before retrying.')
        }
        throw new Error(sdkErrorMessage(error, 'Failed to bind identifier.'))
      }
    },
    [getClient]
  )

  return { bindIdentifier }
}
